// Generated by tutorials/record.cjs from tutorials/articles/h53-special-events.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h53 --text-only` (wording) or
// `node tutorials/record.cjs h53` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'special-events',
  catalogId: 'h53',
  title: 'Special events',
  summary: 'Keep the kitchen\'s list of parties and event meals: what the event is, when and where, how many guests, when to deliver and pick up, and the extras it needs.',
  audience: 'Dietary managers and dietary staff who plan parties and event meals',
  area: 'menus',
  learn: [
    'Add a special event: name, date, meal or time, place and number of guests',
    'Set delivery and pickup times',
    'List the extras the event needs as special requests',
    'Change, print or delete an event',
    'What a special event does and does not change',
  ],
  keywords: [
    'special event',
    'special events',
    'party',
    'birthday party',
    'holiday party',
    'event meal',
    'catering',
    'tea',
    'luncheon',
    'guests',
    'delivery time',
    'pickup time',
    'location',
    'special request',
    'special requests',
    'requisition',
    'requisition form',
    'tablecloths',
    'coffee service',
  ],
  steps: [
    {
      id: 'list',
      part: 'Find your special events',
      heading: 'Open Special Events',
      text: 'Select Menus in the menu on the left, then Special Events.\n\nEach row is one event: its Name, Date and Time (the meal, or the event\'s own time), when the food is delivered and picked up, the Location, and how many Guests. The newest date is at the top.',
      media: {
        image: {
          src: '/help/special-events/list.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Special Events list with four events, such as Family Night Dinner in the Main Dining Room for 40 guests, with their dates, times and places.',
      },
    },
    {
      id: 'new',
      part: 'Add an event',
      heading: 'Start a new event',
      text: 'Select New at the top left. The Add Special Event box opens.\n\nType the Event Name, such as Willow Unit Holiday Party. A name is the one thing every event needs.',
      media: {
        image: {
          src: '/help/special-events/new.webp',
          width: 1280,
          height: 894,
        },
        clip: {
          src: '/help/special-events/new.mp4',
          width: 1280,
          height: 894,
          durationSec: 5.3,
        },
        alt: 'The Add Special Event box with Event Name Willow Unit Holiday Party typed in, and the Date, Mealtime, Location and Number of Guests fields below it.',
      },
    },
    {
      id: 'when',
      part: 'Add an event',
      heading: 'Pick the date and time',
      text: 'Type the Date or pick it from the calendar.\n\nMealtime is Breakfast, Lunch or Dinner when the event is a meal. For anything else, such as an afternoon party, pick Custom and then the Custom Time it starts.',
      media: {
        image: {
          src: '/help/special-events/when.webp',
          width: 1280,
          height: 968,
        },
        clip: {
          src: '/help/special-events/when.mp4',
          width: 1280,
          height: 968,
          durationSec: 6,
        },
        alt: 'The Date filled in, Mealtime set to Custom and Custom Time set to 2:00 PM.',
      },
    },
    {
      id: 'where',
      part: 'Add an event',
      heading: 'Say where, for how many, and when to deliver',
      text: 'Type the Location and the Number of Guests.\n\nCheck Delivery Time and pick when the food should be there, and check Pickup Time for when the kitchen collects it. Leave them unchecked when they do not apply. The pickup time has to be after the delivery time.',
      media: {
        image: {
          src: '/help/special-events/where.webp',
          width: 1280,
          height: 1012,
        },
        clip: {
          src: '/help/special-events/where.mp4',
          width: 1280,
          height: 1012,
          durationSec: 6,
        },
        alt: 'Location Willow Day Room, Number of Guests 22, Delivery Time 1:30 PM and Pickup Time 3:30 PM, both checked.',
      },
    },
    {
      id: 'requests',
      part: 'Add an event',
      heading: 'List what the event needs',
      text: 'Special Requests, at the bottom, lists the extras: tablecloths, coffee service, a cake. A new event already lists your facility\'s default requests.\n\nSelect Add Request for another one, pick it from the list, and add a Description if the kitchen needs to know more. The trash can removes a request.',
      media: {
        image: {
          src: '/help/special-events/requests.webp',
          width: 1280,
          height: 316,
        },
        clip: {
          src: '/help/special-events/requests.mp4',
          width: 1280,
          height: 316,
          durationSec: 6,
        },
        alt: 'The Special Requests list with Centerpieces added at the top, described as Snowflakes, under it Coffee and tea service and Tablecloths.',
      },
    },
    {
      id: 'save',
      part: 'Add an event',
      heading: 'Save',
      text: 'Select Save. The event is added to the list.',
      media: {
        image: {
          src: '/help/special-events/save.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/special-events/save.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.9,
        },
        alt: 'The Special Events list with Willow Unit Holiday Party at the top: Time 2:00 PM, Delivery 1:30 PM, Pickup 3:30 PM, Willow Day Room, 22 guests.',
      },
    },
    {
      id: 'modify',
      part: 'Change, print or delete',
      heading: 'Change an event',
      text: 'Double-click the event, or select it and then Modify. The Modify Special Event box opens with everything you entered, the special requests too. Change what you need and select Save.',
      media: {
        image: {
          src: '/help/special-events/modify.webp',
          width: 1280,
          height: 994,
        },
        clip: {
          src: '/help/special-events/modify.mp4',
          width: 1280,
          height: 994,
          durationSec: 2,
        },
        alt: 'The Modify Special Event box for Family Night Dinner: Dinner, Main Dining Room, 40 guests, delivery 4:30 PM and pickup 6:30 PM, with its three special requests.',
      },
    },
    {
      id: 'print',
      part: 'Change, print or delete',
      heading: 'Print the requisition form',
      text: 'Select an event, then More actions (the three dots at the top right) and Print Requisition Form.\n\nThis prints your facility\'s blank Special Event Requisition Form: a paper form with your facility\'s name and notes and lines for the requests, for staff to fill in by hand when they ask for an event. It is the same blank form whichever event you select.',
      media: {
        image: {
          src: '/help/special-events/print.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/special-events/print.mp4',
          width: 1280,
          height: 748,
          durationSec: 4,
        },
        alt: 'The More actions menu open on Special Events, showing Print Requisition Form and Delete.',
      },
    },
    {
      id: 'delete',
      part: 'Change, print or delete',
      heading: 'Delete an event',
      text: 'To remove an event, select it, then More actions and Delete, and confirm. This cannot be undone.\n\nPast events can stay on the list: the newest are always at the top.',
      media: {
        image: {
          src: '/help/special-events/delete.webp',
          width: 708,
          height: 334,
        },
        alt: 'The Delete Special Event box asking to delete the event Resident Council Tea, with Cancel and Delete.',
      },
    },
    {
      id: 'what-it-does',
      part: 'Change, print or delete',
      heading: 'What a special event does',
      text: 'A special event is the kitchen\'s plan for the event: what, when, where, for how many, and what extras to bring. Everyone who opens Special Events sees the same list.\n\nIt does not add trays, change tray tickets, or add food to the production reports. Plan the event\'s food and amounts as you usually do. For a holiday meal that changes what residents are served, change the menu instead. See Holiday and one-time menus.',
      textOnly: true,
    },
  ],
  tips: [
    'The search box at the top finds events by name, place or time, so typing "patio" lists every event on the Garden Patio.',
    'Click a column heading to sort by it. The list starts with the newest date first.',
    'A special request that nearly every event needs, such as tablecloths, can be marked Default so it is already listed on each new event. See the notes.',
  ],
  notes: [
    'A special event is the kitchen\'s record of the event. It does not add trays, change tray tickets, or add food to the production reports, so plan the event\'s food and amounts as you usually do.',
    'The special requests you can pick come from Tables, Kitchen & Tray Line, Special Event Requests. Each request has a name and a Default box; Default requests start on every new event.',
    'The header and footer notes on the requisition form, and how many request lines it has, are set in Facility Settings, Labels, under Special Events.',
    'Adding, changing and deleting events each need a higher Special Events level on your role. If New, Modify or Delete is missing, ask your administrator.',
  ],
  related: [
    'guests',
    'holiday-and-one-time-menus',
    'running-a-report',
    'favorites-and-print-queue',
    'getting-around',
  ],
  recorded: '2026-11-03',
};

export default article;
